import { MASTER_PART_TYPE, isNodeSublimable } from "./designerUtils";
import {
    resolvePrintModeStrategy,
    validatePrintByModeStrategy,
    validatePrintByBoundsStrategy,
} from "./designerPrintStrategies";
import {
    shouldHideDesignerPart,
} from "./designerHelpers";

const EXCLUSIVE_TYPES = new Set([
    MASTER_PART_TYPE.PART_TYPE,
    MASTER_PART_TYPE.PART_LEFT,
    MASTER_PART_TYPE.PART_RIGHT,
]);
const FINGER_HOLE_LABEL_PATTERN =
  /(agujero\s*(de\s*)?dedo|finger\s*hole|thumb\s*hole)/i;
const CUFF_LABEL_PATTERN = /\b(puno|punos|cuff|cuffs)\b/i;

const PRINTS_TYPE = Number(MASTER_PART_TYPE.PRINTS);
const PRINT_INSTANCE_SEPARATOR = "::";
const DEFAULT_PRINT_BASE_SIZE_CM = 4;

const toNodeArray = (value) => (Array.isArray(value) ? value : Object.values(value || {}));

const normalizeLabel = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

const getNodeNormalizedLabel = (node) => {
  if (!node) return "";
  const item = node.Item || {};
  return normalizeLabel(
    item.nname || item.name || node.nname || node.name || "",
  );
};

const isFingerHoleNode = (node) => {
  const label = getNodeNormalizedLabel(node);
  if (!label) return false;
  return FINGER_HOLE_LABEL_PATTERN.test(label);
};

const isCuffNode = (node) => {
  const label = getNodeNormalizedLabel(node);
  if (!label) return false;
  return CUFF_LABEL_PATTERN.test(label);
};

const isCuffsWaistNode = (node) => {
  const label = getNodeNormalizedLabel(node);
  if (!label) return false;
  return label.includes("puno") && label.includes("cintura");
};

const hasCuffsWaistAncestor = (key, context) => {
  if (!key || !context) return false;
  let current = normalizePartKey(key);
  while (current && current !== "master") {
    const node = context.nodeByKey?.[current];
    if (isCuffsWaistNode(node)) return true;
    current = context.parentByKey?.[current] || null;
  }
  return false;
};

const isCuffContextPartKey = (context, partKey) => {
  if (!context || !partKey) return false;
  let current = normalizePartKey(partKey);
  while (current && current !== "master") {
    const node = context.nodeByKey?.[current];
    if (isCuffNode(node) || isCuffsWaistNode(node)) return true;
    current = context.parentByKey?.[current] || null;
  }
  return false;
};

const isExclusiveTypeForParent = (nodeType) =>
  EXCLUSIVE_TYPES.has(Number(nodeType));

const normalizePartKey = (key) => String(key);
const getNodeLabel = (node) =>
    node?.Item?.nname
    || node?.Item?.name
    || node?.Item?.nice_name
    || node?.nname
    || node?.name
    || node?.nice_name
    || "";
const shouldHideNodeInDesigner = ({ node, context, partKey = null }) => {
    if (!node || !context) return false;
    const key = normalizePartKey(partKey ?? node.key);
    const parentKey = context.parentByKey?.[key] || null;
    const parentNode = parentKey ? context.nodeByKey?.[normalizePartKey(parentKey)] : null;
    const groupLabel = parentNode?.Item?.nname || parentNode?.Item?.name || "";
    return shouldHideDesignerPart({
        groupLabel,
        partLabel: getNodeLabel(node),
        node,
    });
};
const isExtraNode = (node) => Number(node?.type) === Number(MASTER_PART_TYPE.EXTRA);
const nodeHasImage = (node) => {
    const item = node?.Item || {};
    return !!(item.img_front_t || item.img_front || item.img_back_t || item.img_back);
};
// Un "parte de parte" (tipo 8) CON imagen y REGIÓN REAL es estructural: se pinta junto con su
// padre aunque NO sea required (ej. las mangas izq/der de una variante raglan, orejitas de un
// buzo animal). Se EXCLUYE region "none" (tiras/avíos: 274 piezas "Tira N … B y C" del modelo
// viejo, que tienen su propio subsistema y gating) y EXTRA (tipo 5, opcional).
const isStructuralImageSubpart = (node) => {
    if (Number(node?.type) !== Number(MASTER_PART_TYPE.PART_PART)) return false;
    if (!nodeHasImage(node)) return false;
    const region = String(node?.region ?? node?.Item?.region ?? "").toLowerCase();
    return region !== "none";
};
const hasExtraAncestor = (key, context) => {
    if (!key || !context) return false;
    let current = context.parentByKey?.[normalizePartKey(key)] || null;
    while (current && current !== "master") {
        const currentNode = context.nodeByKey?.[normalizePartKey(current)];
        if (isExtraNode(currentNode)) return true;
        current = context.parentByKey?.[normalizePartKey(current)] || null;
    }
    return false;
};
const getPrintLocationKey = (printKey, selection = null) => {
    const explicit = selection?.attrs?.location_key;
    if (explicit !== undefined && explicit !== null && String(explicit) !== "") {
        return String(explicit);
    }
    const normalized = normalizePartKey(printKey);
    const separatorIdx = normalized.indexOf(PRINT_INSTANCE_SEPARATOR);
    if (separatorIdx <= 0) return normalized;
    return normalized.slice(0, separatorIdx);
};

const parseOptionalNumber = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const isFreeformCustomInitialSelection = (initialAttrs = {}) => {
    const printKind = String(initialAttrs?.print_kind || "").trim().toLowerCase();
    return printKind === "image_upload" || printKind === "text";
};


const getInitialPrintWidthCm = (print) => {
    const preferred = DEFAULT_PRINT_BASE_SIZE_CM;
    const minWidth = parseOptionalNumber(print?.min_width ?? print?.min_ancho ?? print?.minw);
    const maxWidth = parseOptionalNumber(print?.max_width ?? print?.max_ancho ?? print?.maxw);
    let width = preferred;
    if (minWidth !== null) width = Math.max(width, minWidth);
    if (maxWidth !== null) width = Math.min(width, maxWidth);
    return width > 0 ? width : preferred;
};

const cloneSelectionState = (state) => ({
    ...state,
    selectedKeys: new Set(state.selectedKeys),
    selectedMaterials: { ...state.selectedMaterials },
    selectedColors: { ...state.selectedColors },
    selectedPrints: { ...state.selectedPrints },
    selectedFullprints: { ...state.selectedFullprints },
    partSublimation: { ...state.partSublimation },
    printFilters: { ...state.printFilters },
});

const addKeyWithParents = (setRef, key, parentByKey) => {
    let current = normalizePartKey(key);
    while (current && current !== "master") {
        setRef.add(current);
        current = parentByKey[current] ? normalizePartKey(parentByKey[current]) : null;
    }
};

const clearPartDerivedData = (next, key) => {
    delete next.selectedMaterials[key];
    delete next.selectedFullprints[key];
    delete next.partSublimation[key];
    delete next.printFilters[key];

    const removedPrintKeys = [];
    Object.keys(next.selectedPrints).forEach((printKey) => {
        const locationKey = getPrintLocationKey(printKey, next.selectedPrints[printKey]);
        if (printKey === key || locationKey === key) {
            removedPrintKeys.push(printKey);
            delete next.selectedPrints[printKey];
        }
    });

    Object.keys(next.selectedColors).forEach((colorKey) => {
        if (colorKey.startsWith(`${key}-`)) {
            delete next.selectedColors[colorKey];
        }
    });
    removedPrintKeys.forEach((printKey) => {
        Object.keys(next.selectedColors).forEach((colorKey) => {
            if (colorKey.startsWith(`${printKey}-print-color`)) {
                delete next.selectedColors[colorKey];
            }
        });
    });
};

const clearPartColorSelection = (next, partKey) => {
  Object.keys(next.selectedColors).forEach((colorKey) => {
    if (
      colorKey.startsWith(`${partKey}-`) &&
      !colorKey.includes("-print-color")
    ) {
      delete next.selectedColors[colorKey];
    }
  });
};

const findFirstActiveCuffSelection = (next, context) => {
  if (!next || !context) return null;
  const entries = Object.entries(context.nodeByKey || {});
  for (const [key, node] of entries) {
    if (!key || key === "master") continue;
    if (!next.selectedKeys.has(key)) continue;
    if (!isCuffContextPartKey(context, key)) continue;
    const materialId = next.selectedMaterials[key] || null;
    if (!materialId) continue;
    const colorObj = next.selectedColors[`${key}-${materialId}`] || null;
    if (!colorObj) continue;
    return { partKey: key, materialId, colorObj };
  }
  return null;
};

const applyFingerHoleInheritanceForPart = (
  next,
  context,
  targetPartKey,
  source,
) => {
  if (!next || !context || !targetPartKey || !source) return false;
  const targetNode = context.nodeByKey?.[targetPartKey];
  if (!targetNode || !isFingerHoleNode(targetNode)) return false;

  const sourceMaterialId = source.materialId || null;
  const sourceColorObj = source.colorObj || null;
  if (!sourceMaterialId || !sourceColorObj) return false;

  const targetMaterials = toNodeArray(targetNode.materials);
  const materialCompatible = targetMaterials.some(
    (material) => String(material?.id) === String(sourceMaterialId),
  );
  const fallbackTargetMaterialId = targetMaterials[0]?.id ?? null;
  const targetMaterialId = materialCompatible
    ? sourceMaterialId
    : next.selectedMaterials[targetPartKey] || fallbackTargetMaterialId;

  if (!targetMaterialId) return false;

  next.selectedMaterials[targetPartKey] = targetMaterialId;
  clearPartColorSelection(next, targetPartKey);
  next.selectedColors[`${targetPartKey}-${targetMaterialId}`] = sourceColorObj;
  return true;
};

const syncFingerHoleWithFirstCuff = (next, context) => {
  if (!next || !context) return;
  const source = findFirstActiveCuffSelection(next, context);
  if (!source) return;

  Object.entries(context.nodeByKey || {}).forEach(([targetPartKey, node]) => {
    if (!targetPartKey || targetPartKey === "master") return;
    if (!next.selectedKeys.has(targetPartKey)) return;
    if (!isFingerHoleNode(node)) return;
    applyFingerHoleInheritanceForPart(next, context, targetPartKey, source);
  });
};

const dropSubtreeSelection = (next, key, nodeByKey) => {
    const stack = [normalizePartKey(key)];
    while (stack.length > 0) {
        const current = stack.pop();
        if (!current) continue;
        next.selectedKeys.delete(current);
        clearPartDerivedData(next, current);
        const node = nodeByKey[current];
        (node?.children || []).forEach((child) => stack.push(normalizePartKey(child.key)));
    }
};

const ensureRequiredDescendantsSelected = (next, node, context) => {
    if (!node || !context) return;

    const handledExclusiveGroups = new Set();
    (node.children || []).forEach((child) => {
        if (isExtraNode(child)) return;
        const childKey = normalizePartKey(child.key);
        const groupId = context.keyToExclusiveGroup?.[childKey];

        if (groupId) {
            if (handledExclusiveGroups.has(groupId)) return;
            handledExclusiveGroups.add(groupId);

            const group = context.exclusiveGroups?.[groupId];
            if (!group || group.requiredKeys?.size === 0) return;

            const keepKey = group.keys.find((candidate) => next.selectedKeys.has(normalizePartKey(candidate)))
                || group.keys.find((candidate) => context.nodeByKey[normalizePartKey(candidate)]?.selected)
                || group.keys.find((candidate) => group.requiredKeys.has(normalizePartKey(candidate)))
                || group.keys[0];

            if (!keepKey) return;

            const normalizedKeep = normalizePartKey(keepKey);
            addKeyWithParents(next.selectedKeys, normalizedKeep, context.parentByKey);
            const keepNode = context.nodeByKey[normalizedKeep];
            ensureRequiredDescendantsSelected(next, keepNode, context);

            group.keys.forEach((candidate) => {
                const normalized = normalizePartKey(candidate);
                if (normalized === normalizedKeep) return;
                dropSubtreeSelection(next, normalized, context.nodeByKey);
            });
            return;
        }

        // Además de los required, arrastramos los "parte de parte" (tipo 8) con imagen: son
        // estructurales y se pintan con su padre (sin esto no se podían pintar, ej. 1401/1402).
        if (!child.required && !isStructuralImageSubpart(child)) return;
        addKeyWithParents(next.selectedKeys, childKey, context.parentByKey);
        ensureRequiredDescendantsSelected(next, child, context);
    });
};

const inferDefaultLeaf = (node) => {
    if (!node) return null;
    if (!node.children || node.children.length === 0) return node;

    const eligibleChildren = (node.children || []).filter((child) => !isExtraNode(child));
    if (!eligibleChildren.length) return null;

    const exclusiveChildren = eligibleChildren.filter((child) => EXCLUSIVE_TYPES.has(Number(child.type)));
    if (exclusiveChildren.length > 0) {
        const preferred = exclusiveChildren.find((child) => child.required || child.selected) || exclusiveChildren[0];
        return inferDefaultLeaf(preferred) || preferred;
    }

    const requiredChild = eligibleChildren.find((child) => child.required);
    if (requiredChild) return inferDefaultLeaf(requiredChild) || requiredChild;

    return inferDefaultLeaf(eligibleChildren[0]) || eligibleChildren[0];
};

export const inferPrintMode = (print) => {
    if (!print) return "image";
    return resolvePrintModeStrategy(print);
};

export const createDesignerInitialState = () => ({
    selectedKeys: new Set(),
    selectedMaterials: {},
    selectedColors: {},
    selectedPrints: {},
    selectedFullprints: {},
    partSublimation: {},
    printFilters: {},
    generalMaterialId: null,
    generalSublimation: false,
    generalFullprint: null,
    activePartKey: null,
    activePrintKey: null,
    viewSide: "front",
});

export const buildTreeContext = (root) => {
    const nodeByKey = {};
    const parentByKey = {};
    const depthByKey = {};
    const requiredKeys = new Set();
    const keyToExclusiveGroup = {};
    const exclusiveGroups = {};

    const walk = (node, parentKey = null, depth = 0) => {
        if (!node) return;
        const key = normalizePartKey(node.key);
        nodeByKey[key] = node;
        parentByKey[key] = parentKey ? normalizePartKey(parentKey) : null;
        depthByKey[key] = depth;
        if (node.required) requiredKeys.add(key);
        (node.children || []).forEach((child) => walk(child, key, depth + 1));
    };

    walk(root, null, 0);

    Object.entries(nodeByKey).forEach(([parentKey, parentNode]) => {
        const groupsByType = {};
        (parentNode?.children || []).forEach((child) => {
            const childType = Number(child.type);
            if (!isExclusiveTypeForParent(childType)) return;
            groupsByType[childType] = groupsByType[childType] || [];
            groupsByType[childType].push(String(child.key));
        });

        Object.entries(groupsByType).forEach(([typeKey, keys]) => {
            if (keys.length <= 1) return;
            const groupId = `${parentKey}:${typeKey}`;
            const requiredInGroup = keys.filter((key) => !!nodeByKey[key]?.required);
            exclusiveGroups[groupId] = {
                id: groupId,
                parentKey,
                type: Number(typeKey),
                keys,
                requiredKeys: new Set(requiredInGroup),
            };
            keys.forEach((key) => {
                keyToExclusiveGroup[key] = groupId;
            });
        });
    });

    return {
        nodeByKey,
        parentByKey,
        depthByKey,
        requiredKeys,
        keyToExclusiveGroup,
        exclusiveGroups,
    };
};

export const buildDefaultSelectedKeys = (root, context) => {
    const selected = new Set();
    const { parentByKey, requiredKeys } = context;

    const pickSelectedFromTree = (node) => {
        if (!node) return;
        if (isExtraNode(node)) return;
        const key = normalizePartKey(node.key);
        if (node.selected) {
            addKeyWithParents(selected, key, parentByKey);
        }
        (node.children || []).forEach(pickSelectedFromTree);
    };

    pickSelectedFromTree(root);

    requiredKeys.forEach((key) => {
        if (isExtraNode(context.nodeByKey[String(key)])) return;
        if (hasExtraAncestor(key, context)) return;
        addKeyWithParents(selected, key, parentByKey);
    });

    (root?.children || []).forEach((top) => {
        if (Number(top.type) !== MASTER_PART_TYPE.PART) return;
        addKeyWithParents(selected, top.key, parentByKey);
        const leaf = inferDefaultLeaf(top);
        if (leaf) addKeyWithParents(selected, leaf.key, parentByKey);
    });

    const dropSubtreeFromSet = (setRef, key) => {
        const stack = [String(key)];
        while (stack.length > 0) {
            const current = stack.pop();
            if (!current) continue;
            setRef.delete(current);
            const node = context.nodeByKey[current];
            (node?.children || []).forEach((child) => stack.push(String(child.key)));
        }
    };

    Object.values(context.exclusiveGroups || {}).forEach((group) => {
        const parentNode = context.nodeByKey[String(group.parentKey)];
        const isCuffsWaistGroup = isCuffsWaistNode(parentNode);
        if (!isCuffsWaistGroup && isExtraNode(parentNode)) return;
        if (!isCuffsWaistGroup && hasExtraAncestor(group.parentKey, context))
          return;
        const selectedInGroup = group.keys.filter((key) => selected.has(String(key)));
        let keepKey = null;
        if (selectedInGroup.length > 0) {
            keepKey = selectedInGroup.find((key) => context.nodeByKey[key]?.selected)
                || selectedInGroup.find((key) => group.requiredKeys.has(String(key)))
                || selectedInGroup[0];
        } else {
            keepKey = group.keys.find((key) => context.nodeByKey[key]?.selected)
                || group.keys.find((key) => group.requiredKeys.has(String(key)))
                || group.keys[0];
            if (keepKey) addKeyWithParents(selected, keepKey, parentByKey);
        }

        group.keys.forEach((key) => {
            const normalized = String(key);
            if (normalized === String(keepKey)) return;
            dropSubtreeFromSet(selected, normalized);
        });
    });

    Object.entries(context.nodeByKey || {}).forEach(
      ([parentKey, parentNode]) => {
        if (!parentNode || parentKey === "master") return;
        if (
          !isCuffsWaistNode(parentNode)
          && !hasCuffsWaistAncestor(parentKey, context)
        ) return;
        const childKeys = (parentNode.children || []).map((child) =>
          String(child.key),
        );
        if (childKeys.length <= 1) return;

        const selectedChildren = childKeys.filter((key) => selected.has(key));
        if (selectedChildren.length <= 1) return;

        const keepKey =
          selectedChildren.find((key) => !!context.nodeByKey[key]?.selected) ||
          selectedChildren.find((key) => !!context.nodeByKey[key]?.required) ||
          selectedChildren[0];

        selectedChildren.forEach((key) => {
          if (String(key) === String(keepKey)) return;
          dropSubtreeFromSet(selected, key);
        });
      },
    );

    // Estructurales: todo "parte de parte" (tipo 8) con imagen cuyo padre quedó seleccionado se
    // pinta con él, aunque no sea required. Iterativo para cadenas parte→parte de parte→…
    // Corre DESPUÉS de los dedup excluyentes, así solo agrega hijos de variantes que quedaron.
    let addedStructural = true;
    while (addedStructural) {
        addedStructural = false;
        Object.entries(context.nodeByKey || {}).forEach(([parentKey, parentNode]) => {
            if (parentKey === "master" || !selected.has(String(parentKey))) return;
            (parentNode?.children || []).forEach((child) => {
                const childKey = String(child.key);
                if (selected.has(childKey)) return;
                if (isExtraNode(child) || hasExtraAncestor(childKey, context)) return;
                if (!isStructuralImageSubpart(child)) return;
                addKeyWithParents(selected, childKey, parentByKey);
                addedStructural = true;
            });
        });
    }

    // Default determinístico de "Cambiar detalles": en un grupo excluyente SIN nada aplicado por
    // el backend (node.selected) ni required, la opción por defecto debe ser la PRIMERA (la que se
    // muestra primero). Corrige casos donde quedaba elegida una variante posterior (ej. "Cuello
    // redondo 3 líneas" en vez de "Cuello redondo").
    Object.values(context.exclusiveGroups || {}).forEach((group) => {
        const keys = Array.isArray(group?.keys) ? group.keys.map(String) : [];
        if (keys.length <= 1) return;
        const anyBackendSelected = keys.some((k) => !!context.nodeByKey[k]?.selected);
        const anyRequired = keys.some((k) => group.requiredKeys?.has(String(k)));
        if (anyBackendSelected || anyRequired) return; // respetar lo aplicado/obligatorio
        const parentKey = String(group.parentKey || "");
        if (parentKey && parentKey !== "master" && !selected.has(parentKey)) return; // grupo no activo
        const chosen = keys.filter((k) => selected.has(k));
        if (chosen.length === 0) return; // el grupo no tiene default → no forzamos
        const first = keys[0];
        if (chosen.length === 1 && String(chosen[0]) === first) return; // ya es la primera
        keys.forEach((k) => { if (k !== first) dropSubtreeFromSet(selected, k); });
        addKeyWithParents(selected, first, parentByKey);
    });

    return selected;
};

export const isMaterialCompatible = (node, materialId) => {
    if (!node || !materialId) return false;
    const materials = toNodeArray(node.materials);
    return materials.some((m) => String(m.id) === String(materialId));
};

export const getPartMaterialId = (state, node, partKey) => {
    const explicitMaterial = state.selectedMaterials[partKey];
    if (explicitMaterial) return explicitMaterial;
    if (state.generalMaterialId && isMaterialCompatible(node, state.generalMaterialId)) return state.generalMaterialId;
    return null;
};

export const getSelectedPartColor = (state, partKey, materialId) => {
    if (!materialId) return null;
    return state.selectedColors[`${partKey}-${materialId}`] || null;
};

export const getPrintNodesForPart = (partNode) => {
    const prints = [];
    const walk = (node) => {
        if (!node) return;
        if (Number(node.type) === PRINTS_TYPE && node?.Item?.display !== "none") {
            prints.push(node);
        }
        (node.children || []).forEach(walk);
    };
    walk(partNode);
    return prints;
};

export const filterPrintsForLocation = ({ locationNode, filters = {}, sublimated = false }) => {
    const source = toNodeArray(locationNode?.prints);
    let list = source
        .filter((print) => !print?.deleted_at)
        .filter((print) => Number(print?.web ?? 0) === 1);

    if (sublimated) {
        list = list
            .filter((print) => Number(print?.sublimable ?? 0) === 1)
            .filter((print) => Number(print?.master_print_type ?? 0) !== 2);
    }

    if (filters.mode === "text") {
        list = list.filter((print) => Number(print?.max_letters || 0) > 0);
    } else if (filters.mode === "image") {
        list = list.filter((print) => Number(print?.max_letters || 0) === 0);
    }

    if (filters.scope === "personal") {
        list = list.filter((print) => Number(print?.personal || 0) === 1);
    } else if (filters.scope === "group") {
        list = list.filter((print) => Number(print?.personal || 0) === 0);
    }

    if (filters.technique) {
        const technique = Number(filters.technique);
        list = list.filter((print) => Number(print?.master_print_type || 0) === technique);
    }

    return list;
};

export const validatePrintSelection = ({ selection, details }) => {
    if (!selection?.print) return ["Seleccioná un diseño."];

    const print = selection.print;
    const mode = selection.mode || inferPrintMode(print);
    const errors = [];

    validatePrintByModeStrategy({ selection, details, mode, errors });
    validatePrintByBoundsStrategy({ selection, details, errors });
    return errors;
};

export const getPartCompletion = ({ node, partKey, state, allColors = [] }) => {
    const required = !!node?.required;
    const selected = state.selectedKeys.has(partKey);
    const missing = [];

    if (!selected && required) {
        missing.push("Seleccionar parte");
        return { selected, complete: false, missing };
    }
    if (!selected) return { selected, complete: true, missing: [] };

    const materials = toNodeArray(node?.materials);
    if (materials.length > 0) {
        const materialId = getPartMaterialId(state, node, partKey);
        if (!materialId) {
            missing.push("Material");
        } else if (state.partSublimation[partKey] && isNodeSublimable(node)) {
            if (!state.selectedFullprints[partKey]) {
                missing.push("Fullprint");
            }
        } else {
            const hasCompatibleChart = allColors.some((color) =>
                (color?.color_charts || []).some((chart) => String(chart?.material_id) === String(materialId))
            );
            if (hasCompatibleChart) {
                const selectedColor = getSelectedPartColor(state, partKey, materialId);
                if (!selectedColor) missing.push("Color");
            }
        }
    }

    return { selected, complete: missing.length === 0, missing };
};

export const collectBlockingIssues = ({ root, context, state, allColors = [], printDetails = {} }) => {
    const issues = [];
    const { nodeByKey, requiredKeys } = context;
    const handledRequiredKeys = new Set();

    Object.values(context.exclusiveGroups || {}).forEach((group) => {
        if (!group || group.requiredKeys.size === 0) return;
        const visibleGroupKeys = group.keys.filter((groupKey) => !shouldHideNodeInDesigner({
            node: nodeByKey[String(groupKey)],
            context,
            partKey: groupKey,
        }));
        if (visibleGroupKeys.length === 0) return;
        const groupParentKey = String(group.parentKey || "");
        if (groupParentKey && groupParentKey !== "master" && !state.selectedKeys.has(groupParentKey)) {
            return;
        }
        visibleGroupKeys.forEach((key) => handledRequiredKeys.add(String(key)));
        const anySelected = visibleGroupKeys.some((key) => state.selectedKeys.has(String(key)));
        if (anySelected) return;
        const parentNode = group.parentKey ? nodeByKey[String(group.parentKey)] : null;
        const parentLabel = parentNode?.Item?.nname || parentNode?.Item?.name || `Parte ${group.parentKey}`;
        issues.push({
            code: "missing_required_exclusive_part",
            key: group.id,
            message: `${parentLabel}: falta seleccionar una variante requerida.`,
        });
    });

    requiredKeys.forEach((requiredKey) => {
        const key = String(requiredKey);
        const node = nodeByKey[key];
        if (isExtraNode(node)) return;
        if (shouldHideNodeInDesigner({ node, context, partKey: key })) return;
        if (handledRequiredKeys.has(key)) return;
        const parentKey = String(context.parentByKey?.[key] || "");
        if (parentKey && parentKey !== "master" && !state.selectedKeys.has(parentKey)) return;
        if (!state.selectedKeys.has(key)) {
            issues.push({
                code: "missing_required_part",
                key,
                message: `Falta seleccionar la parte requerida ${key}.`,
            });
        }
    });

    Object.entries(nodeByKey).forEach(([key, node]) => {
        if (key === "master") return;
        if (!state.selectedKeys.has(key)) return;
        if (Number(node.type) === PRINTS_TYPE) return;
        if (node?.Item?.display === "none") return;
        if (shouldHideNodeInDesigner({ node, context, partKey: key })) return;
        const completion = getPartCompletion({ node, partKey: key, state, allColors });
        if (!completion.complete) {
            const label = node?.Item?.nname || node?.Item?.name || `Parte ${key}`;
            issues.push({
                code: "incomplete_part",
                key,
                message: `${label}: falta ${completion.missing.join(", ")}.`,
            });
        }
    });

    Object.entries(nodeByKey).forEach(([key, node]) => {
        if (Number(node.type) !== PRINTS_TYPE) return;
        const selections = Object.entries(state.selectedPrints || {})
            .filter(([printKey, selection]) => getPrintLocationKey(printKey, selection) === key)
            .map(([, selection]) => selection)
            .filter((selection) => !!selection?.print);
        if (!state.selectedKeys.has(key) && !node.required && selections.length === 0) return;
        if (selections.length === 0) {
            if (node.required) {
                const label = node?.Item?.nname || node?.Item?.name || "Ubicación de diseño";
                issues.push({
                    code: "missing_required_print",
                    key,
                    message: `${label}: falta seleccionar diseño.`,
                });
            }
            return;
        }
        selections.forEach((selection) => {
            const details = printDetails[selection.print.id] || selection.print;
            const errors = validatePrintSelection({ selection, details });
            errors.forEach((message) => {
                issues.push({
                    code: "invalid_print",
                    key,
                    message,
                });
            });
        });
    });

    return issues;
};

export const getWizardPartNodes = ({ root, context, selectedKeys }) => {
    const list = [];
    if (!root) return list;

    const walk = (node) => {
        if (!node || node.key === "master") return;
        if (node?.Item?.display === "none") return;

        const key = normalizePartKey(node.key);
        const parentKey = context.parentByKey[key];
        if (shouldHideNodeInDesigner({ node, context, partKey: key })) return;
        const isPrintNode = Number(node.type) === PRINTS_TYPE;
        const parentSelected = !parentKey || parentKey === "master" || selectedKeys.has(parentKey);
        // Si el padre no está activo, no mostramos esta rama aunque el nodo sea requerido.
        // Esto evita exigir hijos requeridos de variantes excluyentes no seleccionadas.
        const visible = selectedKeys.has(key) || parentSelected;

        if (!isPrintNode && visible) {
            list.push({
                key,
                node,
                parentKey,
                depth: context.depthByKey[key] || 0,
            });
        }

        (node.children || []).forEach(walk);
    };

    (root.children || []).forEach(walk);
    return list;
};

export const validatePayloadShape = (payload) => {
    if (!payload || typeof payload !== "object") {
        return { ok: false, error: "Payload vacío." };
    }
    if (!Array.isArray(payload.tree) || payload.tree.length === 0) {
        return { ok: false, error: "tree debe ser un arreglo no vacío." };
    }
    const masterNode = payload.tree[0];
    if (String(masterNode?.key) !== "master") {
        return { ok: false, error: "tree[0].key debe ser master." };
    }
    if (!Array.isArray(payload.applied_parts)) {
        return { ok: false, error: "applied_parts debe ser un arreglo." };
    }
    for (const row of payload.applied_parts) {
        if (!row || row.master_part_id === undefined || row.master_part_id === null) {
            return { ok: false, error: "Cada applied_part debe tener master_part_id." };
        }
        if (row.attrs !== undefined && (row.attrs === null || typeof row.attrs !== "object")) {
            return { ok: false, error: "attrs debe ser objeto si está presente." };
        }
    }
    if (payload.designer_meta !== undefined) {
        if (payload.designer_meta === null || typeof payload.designer_meta !== "object") {
            return { ok: false, error: "designer_meta debe ser un objeto." };
        }
        const customCuts = payload.designer_meta?.custom_cuts;
        if (customCuts !== undefined) {
            if (customCuts === null || typeof customCuts !== "object") {
                return { ok: false, error: "designer_meta.custom_cuts debe ser un objeto." };
            }
            if (!Array.isArray(customCuts.front) || !Array.isArray(customCuts.back)) {
                return { ok: false, error: "designer_meta.custom_cuts.front/back deben ser arreglos." };
            }
        }
    }
    return { ok: true };
};

export const DESIGNER_ACTIONS = Object.freeze({
    INIT: "INIT",
    RESTORE_DRAFT: "RESTORE_DRAFT",
    RESET: "RESET",
    SET_VIEW_SIDE: "SET_VIEW_SIDE",
    SET_ACTIVE_PART: "SET_ACTIVE_PART",
    SET_ACTIVE_PRINT: "SET_ACTIVE_PRINT",
    TOGGLE_PART: "TOGGLE_PART",
    SELECT_MATERIAL: "SELECT_MATERIAL",
    SELECT_COLOR: "SELECT_COLOR",
    CLEAR_COLOR: "CLEAR_COLOR",
    TOGGLE_SUBLIMATION: "TOGGLE_SUBLIMATION",
    SELECT_FULLPRINT: "SELECT_FULLPRINT",
    SET_GENERAL_MATERIAL: "SET_GENERAL_MATERIAL",
    SET_GENERAL_SUBLIMATION: "SET_GENERAL_SUBLIMATION",
    SET_GENERAL_FULLPRINT: "SET_GENERAL_FULLPRINT",
    APPLY_GENERAL_MATERIAL: "APPLY_GENERAL_MATERIAL",
    APPLY_GENERAL_SUBLIMATION: "APPLY_GENERAL_SUBLIMATION",
    SET_PRINT_FILTER: "SET_PRINT_FILTER",
    SELECT_PRINT: "SELECT_PRINT",
    CLEAR_PRINT: "CLEAR_PRINT",
    SET_PRINT_MODE: "SET_PRINT_MODE",
    SET_PRINT_TEXT: "SET_PRINT_TEXT",
    SET_PRINT_IMAGE: "SET_PRINT_IMAGE",
    SET_PRINT_FONT: "SET_PRINT_FONT",
    SET_PRINT_COLOR: "SET_PRINT_COLOR",
    MERGE_PRINT_ATTRS: "MERGE_PRINT_ATTRS",
});

export const designerReducer = (state, action) => {
    switch (action.type) {
        case DESIGNER_ACTIONS.RESET:
            return createDesignerInitialState();

        case DESIGNER_ACTIONS.INIT: {
            const base = createDesignerInitialState();
            return {
                ...base,
                selectedKeys: new Set(action.selectedKeys || []),
                activePartKey: action.activePartKey ? normalizePartKey(action.activePartKey) : null,
                viewSide: action.viewSide || "front",
            };
        }

        case DESIGNER_ACTIONS.RESTORE_DRAFT: {
            const draft = action.draft || {};
            return {
                ...createDesignerInitialState(),
                selectedKeys: new Set(draft.selectedKeys || []),
                selectedMaterials: { ...(draft.selectedMaterials || {}) },
                selectedColors: { ...(draft.selectedColors || {}) },
                selectedPrints: { ...(draft.selectedPrints || {}) },
                selectedFullprints: { ...(draft.selectedFullprints || {}) },
                partSublimation: { ...(draft.partSublimation || {}) },
                printFilters: { ...(draft.printFilters || {}) },
                generalMaterialId: draft.generalMaterialId || null,
                generalSublimation: !!draft.generalSublimation,
                generalFullprint: draft.generalFullprint || null,
                activePartKey: draft.activePartKey ? normalizePartKey(draft.activePartKey) : null,
                activePrintKey: draft.activePrintKey ? normalizePartKey(draft.activePrintKey) : null,
                viewSide: draft.viewSide === "back" ? "back" : "front",
            };
        }

        case DESIGNER_ACTIONS.SET_VIEW_SIDE: {
            const nextViewSide = action.viewSide === "back" ? "back" : "front";
            if (state.viewSide === nextViewSide) return state;
            return { ...state, viewSide: nextViewSide };
        }

        case DESIGNER_ACTIONS.SET_ACTIVE_PART: {
            const nextActivePart = action.partKey ? normalizePartKey(action.partKey) : null;
            if (state.activePartKey === nextActivePart) return state;
            return { ...state, activePartKey: nextActivePart };
        }

        case DESIGNER_ACTIONS.SET_ACTIVE_PRINT: {
            const nextActivePrint = action.printKey ? normalizePartKey(action.printKey) : null;
            if (state.activePrintKey === nextActivePrint) return state;
            return { ...state, activePrintKey: nextActivePrint };
        }

        case DESIGNER_ACTIONS.TOGGLE_PART: {
            const { partKey, selected, context } = action;
            const key = normalizePartKey(partKey);
            if (selected && state.selectedKeys.has(key)) return state;
            if (!selected && !state.selectedKeys.has(key)) return state;
            const next = cloneSelectionState(state);
            const required = !!action.requiredKeys?.has(key);
            const node = context.nodeByKey[key];
            if (!node) return state;

            if (!selected && required) return state;

            if (selected) {
                addKeyWithParents(next.selectedKeys, key, context.parentByKey);
                const nodeType = Number(node.type);
                const parentKey = context.parentByKey[key];
                const parentNode = parentKey
                  ? context.nodeByKey[parentKey]
                  : null;
                if (isExclusiveTypeForParent(nodeType)) {
                  (parentNode?.children || []).forEach((sibling) => {
                    const siblingKey = normalizePartKey(sibling.key);
                    if (siblingKey === key) return;
                    if (Number(sibling.type) !== nodeType) return;
                    dropSubtreeSelection(next, siblingKey, context.nodeByKey);
                  });
                }

                ensureRequiredDescendantsSelected(next, node, context);

                let inheritedFromCuff = false;
                if (isFingerHoleNode(node)) {
                  const source = findFirstActiveCuffSelection(next, context);
                  if (source) {
                    inheritedFromCuff = applyFingerHoleInheritanceForPart(
                      next,
                      context,
                      key,
                      source,
                    );
                  }
                }

                // Aplicación por defecto de material/configuración general al activar una parte.
                const nodeMaterials = toNodeArray(node.materials);
                const generalMaterialId = state.generalMaterialId;
                const canUseGeneralMaterial =
                    generalMaterialId &&
                    nodeMaterials.some((m) => String(m.id) === String(generalMaterialId));

                if (canUseGeneralMaterial && !inheritedFromCuff) {
                  next.selectedMaterials[key] = generalMaterialId;

                  if (
                    state.generalSublimation &&
                    state.generalFullprint &&
                    isNodeSublimable(node)
                  ) {
                    next.partSublimation[key] = true;
                    next.selectedFullprints[key] = state.generalFullprint;
                    Object.keys(next.selectedColors).forEach((colorKey) => {
                      if (
                        colorKey.startsWith(`${key}-`) &&
                        !colorKey.includes("-print-color")
                      ) {
                        delete next.selectedColors[colorKey];
                      }
                    });
                  }
                }
            } else {
                dropSubtreeSelection(next, key, context.nodeByKey);
            }

            if (next.activePartKey && !next.selectedKeys.has(next.activePartKey)) {
                next.activePartKey = null;
            }
            if (next.activePrintKey && !next.selectedKeys.has(next.activePrintKey)) {
                next.activePrintKey = null;
            }

            return next;
        }

        case DESIGNER_ACTIONS.SELECT_MATERIAL: {
            const key = normalizePartKey(action.partKey);
            const materialId = action.materialId || null;
            const currentMaterialId = state.selectedMaterials[key] || null;
            const sameMaterial = String(currentMaterialId || "") === String(materialId || "");
            const alreadySelected = state.selectedKeys.has(key);
            if (sameMaterial && alreadySelected) {
                return state;
            }
            const next = cloneSelectionState(state);

            next.selectedKeys.add(key);
            if (materialId) {
                next.selectedMaterials[key] = materialId;
                clearPartColorSelection(next, key);
            } else {
                delete next.selectedMaterials[key];
                clearPartColorSelection(next, key);
                delete next.selectedFullprints[key];
                delete next.partSublimation[key];
            }

            if (action.context) {
              if (isCuffContextPartKey(action.context, key)) {
                syncFingerHoleWithFirstCuff(next, action.context);
              }
            }
            return next;
        }

        case DESIGNER_ACTIONS.SELECT_COLOR: {
            const next = cloneSelectionState(state);
            const key = normalizePartKey(action.partKey);
            const materialId = action.materialId;
            const colorObj = action.colorObj;
            if (!materialId || !colorObj) return state;

            next.selectedKeys.add(key);
            next.selectedMaterials[key] = materialId;
            clearPartColorSelection(next, key);
            next.selectedColors[`${key}-${materialId}`] = colorObj;
            delete next.selectedFullprints[key];
            next.partSublimation[key] = false;

            if (action.context) {
              if (isCuffContextPartKey(action.context, key)) {
                syncFingerHoleWithFirstCuff(next, action.context);
              }
            }
            return next;
        }

        case DESIGNER_ACTIONS.CLEAR_COLOR: {
            const key = normalizePartKey(action.partKey);
            const materialId = action.materialId || state.selectedMaterials[key] || null;
            if (!materialId) return state;
            const colorKey = `${key}-${materialId}`;
            if (!Object.prototype.hasOwnProperty.call(state.selectedColors, colorKey)) return state;
            const next = cloneSelectionState(state);
            delete next.selectedColors[colorKey];
            return next;
        }

        case DESIGNER_ACTIONS.TOGGLE_SUBLIMATION: {
            const next = cloneSelectionState(state);
            const key = normalizePartKey(action.partKey);
            const enabled = !!action.enabled;
            next.selectedKeys.add(key);
            next.partSublimation[key] = enabled;

            if (enabled) {
                Object.keys(next.selectedColors).forEach((colorKey) => {
                    if (colorKey.startsWith(`${key}-`) && !colorKey.includes("-print-color")) {
                        delete next.selectedColors[colorKey];
                    }
                });
            } else {
                delete next.selectedFullprints[key];
            }
            return next;
        }

        case DESIGNER_ACTIONS.SELECT_FULLPRINT: {
            const next = cloneSelectionState(state);
            const key = normalizePartKey(action.partKey);
            const fullprint = action.fullprint || null;
            next.selectedKeys.add(key);
            if (fullprint) {
                next.selectedFullprints[key] = fullprint;
                next.partSublimation[key] = true;
                Object.keys(next.selectedColors).forEach((colorKey) => {
                    if (colorKey.startsWith(`${key}-`) && !colorKey.includes("-print-color")) {
                        delete next.selectedColors[colorKey];
                    }
                });
            } else {
                delete next.selectedFullprints[key];
                next.partSublimation[key] = false;
            }
            return next;
        }

        case DESIGNER_ACTIONS.SET_GENERAL_MATERIAL: {
            const materialId = action.materialId || null;
            if (!materialId) {
                return {
                    ...state,
                    generalMaterialId: null,
                    generalSublimation: false,
                    generalFullprint: null,
                };
            }
            return {
                ...state,
                generalMaterialId: materialId,
            };
        }

        case DESIGNER_ACTIONS.SET_GENERAL_SUBLIMATION:
            return { ...state, generalSublimation: !!action.enabled };

        case DESIGNER_ACTIONS.SET_GENERAL_FULLPRINT:
            return { ...state, generalFullprint: action.fullprint || null };

        case DESIGNER_ACTIONS.APPLY_GENERAL_MATERIAL: {
            const next = cloneSelectionState(state);
            const materialId = state.generalMaterialId;
            if (!materialId) return state;
            (action.partKeys || []).forEach((partKey) => {
                const key = normalizePartKey(partKey);
                const node = action.context?.nodeByKey?.[key];
                if (!node || !isMaterialCompatible(node, materialId)) return;
                next.selectedMaterials[key] = materialId;
            });
            return next;
        }

        case DESIGNER_ACTIONS.APPLY_GENERAL_SUBLIMATION: {
            const next = cloneSelectionState(state);
            if (!state.generalSublimation || !state.generalFullprint || !state.generalMaterialId) return state;
            (action.partKeys || []).forEach((partKey) => {
                const key = normalizePartKey(partKey);
                next.partSublimation[key] = true;
                if (!next.selectedMaterials[key]) {
                    next.selectedMaterials[key] = state.generalMaterialId;
                }
                Object.keys(next.selectedColors).forEach((colorKey) => {
                    if (colorKey.startsWith(`${key}-`) && !colorKey.includes("-print-color")) {
                        delete next.selectedColors[colorKey];
                    }
                });
                next.selectedFullprints[key] = state.generalFullprint;
            });
            return next;
        }

        case DESIGNER_ACTIONS.SET_PRINT_FILTER: {
            const key = normalizePartKey(action.partKey);
            const current = state.printFilters[key] || {};
            const nextValue = action.value || null;
            if ((current[action.filterName] || null) === nextValue) {
                return state;
            }
            const next = cloneSelectionState(state);
            next.printFilters[key] = {
                ...current,
                [action.filterName]: nextValue,
            };
            return next;
        }

        case DESIGNER_ACTIONS.SELECT_PRINT: {
            const key = normalizePartKey(action.partKey);
            const locationKey = normalizePartKey(action.locationKey || getPrintLocationKey(key));
            const next = cloneSelectionState(state);
            const print = action.print;
            if (!print) return state;

            addKeyWithParents(next.selectedKeys, locationKey, action.context?.parentByKey || {});
            const existing = next.selectedPrints[key];
            const attrs = { ...(existing?.attrs || {}) };
            const initialAttrs = action.initialAttrs && typeof action.initialAttrs === "object"
                ? action.initialAttrs
                : {};
            const isFreeformCustomPrint = isFreeformCustomInitialSelection(initialAttrs);
            const side = action.side;
            if (side !== undefined && side !== null) attrs.side = side;
            attrs.location_key = locationKey;
            if (!attrs.height) {
                attrs.height = String(
                    isFreeformCustomPrint
                        ? DEFAULT_PRINT_BASE_SIZE_CM
                        : (print.max_height ?? print.min_height ?? "")
                );
            }
            if (!attrs.base_height_cm) {
                attrs.base_height_cm = String(
                    isFreeformCustomPrint
                        ? DEFAULT_PRINT_BASE_SIZE_CM
                        : (print.max_height ?? print.min_height ?? "")
                );
            }
            if (!attrs.max_ancho && !attrs.maxw) {
                attrs.max_ancho = String(isFreeformCustomPrint ? DEFAULT_PRINT_BASE_SIZE_CM : getInitialPrintWidthCm(print));
            }
            if (action.initialAttrs && typeof action.initialAttrs === "object") {
                Object.entries(action.initialAttrs).forEach(([attrKey, attrValue]) => {
                    attrs[attrKey] = attrValue;
                });
            }
            const inferredMode = existing?.mode || inferPrintMode(print);

            next.selectedPrints[key] = {
                print,
                mode: inferredMode,
                attrs,
            };
            next.activePrintKey = key;
            return next;
        }

        case DESIGNER_ACTIONS.CLEAR_PRINT: {
            const key = normalizePartKey(action.partKey);
            const next = cloneSelectionState(state);
            const keysToRemove = Object.keys(next.selectedPrints).filter((printKey) => {
                const locationKey = getPrintLocationKey(printKey, next.selectedPrints[printKey]);
                return printKey === key || locationKey === key;
            });
            keysToRemove.forEach((printKey) => {
                delete next.selectedPrints[printKey];
            });
            Object.keys(next.selectedColors).forEach((colorKey) => {
                if (colorKey.startsWith(`${key}-print-color`)) {
                    delete next.selectedColors[colorKey];
                }
                keysToRemove.forEach((printKey) => {
                    if (colorKey.startsWith(`${printKey}-print-color`)) {
                        delete next.selectedColors[colorKey];
                    }
                });
            });
            if (next.activePrintKey === key || keysToRemove.includes(String(next.activePrintKey))) next.activePrintKey = null;
            return next;
        }

        case DESIGNER_ACTIONS.SET_PRINT_MODE: {
            const key = normalizePartKey(action.partKey);
            const mode = action.mode || "image";
            if (!state.selectedPrints[key]) return state;
            const next = cloneSelectionState(state);
            const current = next.selectedPrints[key];
            const attrs = { ...(current.attrs || {}) };
            if (mode === "image") {
                delete attrs.color;
                delete attrs.color1;
                delete attrs.color2;
                delete attrs.color3;
                Object.keys(next.selectedColors).forEach((colorKey) => {
                    if (colorKey.startsWith(`${key}-print-color`)) delete next.selectedColors[colorKey];
                });
            }
            next.selectedPrints[key] = { ...current, mode, attrs };
            return next;
        }

        case DESIGNER_ACTIONS.SET_PRINT_TEXT: {
            const key = normalizePartKey(action.partKey);
            if (!state.selectedPrints[key]) return state;
            const currentText = String(state.selectedPrints[key]?.attrs?.text || "");
            const nextText = String(action.text || "");
            if (currentText === nextText) return state;
            const next = cloneSelectionState(state);
            next.selectedPrints[key] = {
                ...next.selectedPrints[key],
                attrs: {
                    ...(next.selectedPrints[key].attrs || {}),
                    text: nextText,
                },
            };
            return next;
        }

        case DESIGNER_ACTIONS.SET_PRINT_IMAGE: {
            const key = normalizePartKey(action.partKey);
            if (!state.selectedPrints[key]) return state;
            const currentAttrs = state.selectedPrints[key]?.attrs || {};
            const nextMode = action.imageMode || "url";
            const nextUrl = action.imageUrl || "";
            if (
                String(currentAttrs.image_mode || "url") === String(nextMode)
                && String(currentAttrs.image_url || "") === String(nextUrl)
            ) {
                return state;
            }
            const next = cloneSelectionState(state);
            next.selectedPrints[key] = {
                ...next.selectedPrints[key],
                attrs: {
                    ...(next.selectedPrints[key].attrs || {}),
                    image_mode: nextMode,
                    image_url: nextUrl,
                },
            };
            return next;
        }

        case DESIGNER_ACTIONS.SET_PRINT_FONT: {
            const key = normalizePartKey(action.partKey);
            if (!state.selectedPrints[key]) return state;
            const currentFont = state.selectedPrints[key]?.attrs?.font || null;
            const nextFont = action.fontId || null;
            if (String(currentFont || "") === String(nextFont || "")) return state;
            const next = cloneSelectionState(state);
            next.selectedPrints[key] = {
                ...next.selectedPrints[key],
                attrs: {
                    ...(next.selectedPrints[key].attrs || {}),
                    font: nextFont,
                },
            };
            return next;
        }

        case DESIGNER_ACTIONS.SET_PRINT_COLOR: {
            const key = normalizePartKey(action.partKey);
            if (!state.selectedPrints[key]) return state;
            const colorIndex = Number(action.colorIndex || 1);
            const colorObj = action.colorObj;
            const colorAttrKey = `color${colorIndex}`;
            const next = cloneSelectionState(state);
            const currentColorId = state.selectedPrints[key]?.attrs?.[colorAttrKey] ?? null;
            const nextColorId = colorObj ? (colorObj.id ?? null) : null;
            if (String(currentColorId || "") === String(nextColorId || "")) return state;

            if (colorObj) {
                next.selectedColors[`${key}-print-color${colorIndex}`] = colorObj;
            } else {
                delete next.selectedColors[`${key}-print-color${colorIndex}`];
            }
            const nextAttrs = {
                ...(next.selectedPrints[key].attrs || {}),
            };
            if (colorObj) {
                nextAttrs[colorAttrKey] = nextColorId;
            } else {
                delete nextAttrs[colorAttrKey];
                if (colorIndex === 1) delete nextAttrs.color;
            }
            next.selectedPrints[key] = {
                ...next.selectedPrints[key],
                attrs: nextAttrs,
            };
            return next;
        }

        case DESIGNER_ACTIONS.MERGE_PRINT_ATTRS: {
            const key = normalizePartKey(action.partKey);
            if (!state.selectedPrints[key]) return state;
            const currentAttrs = state.selectedPrints[key]?.attrs || {};
            const incomingAttrs = action.attrs || {};
            const attrEntries = Object.entries(incomingAttrs);
            if (!attrEntries.length) return state;
            const hasChanges = attrEntries.some(([attrKey, attrValue]) =>
                String(currentAttrs[attrKey] ?? "") !== String(attrValue ?? "")
            );
            if (!hasChanges) return state;
            const next = cloneSelectionState(state);
            next.selectedPrints[key] = {
                ...next.selectedPrints[key],
                attrs: {
                    ...currentAttrs,
                    ...incomingAttrs,
                },
            };
            const nextLocationKey = String(next.selectedPrints[key]?.attrs?.location_key || "").trim();
            if (nextLocationKey) {
                addKeyWithParents(next.selectedKeys, nextLocationKey, action.context?.parentByKey || {});
            }
            return next;
        }

        default:
            return state;
    }
};

export const getMissingPrintColors = (selection) => {
    if (!selection?.print) return [];
    const mode = selection.mode || inferPrintMode(selection.print);
    if (mode !== "text") return [];
    const slots = 1;
    const attrs = selection.attrs || {};
    const missing = [];
    for (let i = 1; i <= slots; i += 1) {
        if (i > 1 && !attrs[`color${i}`] && !attrs[`color${i - 1}`]) break;
        if (!attrs[`color${i}`]) missing.push(i);
    }
    return missing;
};
